import { Box, Button, Card, List, ListItem, Typography } from '@mui/material';
import UploadMultiFile from '../../../../../components/upload/UploadMultiFile';
import { AnimatePresence, m } from 'framer-motion';
import React from 'react';
import Iconify from '../../../../../components/Iconify';

const CashFlowImageUploader = ({ files, setFiles, handleImageUpload, documentGallery, openImageViewer, isUploading, allowPdf = false }) => {
  const imageDocuments = (documentGallery || []).filter(isImageDocument);
  const acceptedFileTypes = allowPdf
    ? 'image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf'
    : 'image/jpeg,image/png,.jpg,.jpeg,.png';

  const getImageViewerIndex = (src) => imageDocuments.findIndex((value) => value === src);

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant='subtitle1' padding='10px 0'>
        Related Documents
      </Typography>
      <UploadMultiFile
        error={false}
        showPreview={true}
        files={files}
        onRemove={(removedFile) => {
          setFiles(files.filter((file) => file !== removedFile));
        }}
        onRemoveAll={() => {
          setFiles([]);
        }}
        helperText={''}
        accept={acceptedFileTypes}
        onDrop={(uploadedFiles) => {
          const validFiles = uploadedFiles.filter((file) => isAllowedDocumentFile(file, allowPdf));
          const dropTime = Date.now();

          setFiles((value) => [
            ...value,
            ...validFiles.map((file, index) =>
              Object.assign(file, {
                id: `${dropTime}-${index}-${file.name}`,
                preview: URL.createObjectURL(file),
                isUploading: false,
              })
            ),
          ]);
        }}
        onUpload={() => {
          handleImageUpload();
        }}
        isUploading={isUploading}
      />

      {documentGallery?.length > 0 && (
        <Typography variant='subtitle1' padding='10px 0'>
          Uploaded Documents
        </Typography>
      )}
      <List disablePadding sx={{ my: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}>
        <AnimatePresence>
          {documentGallery
            ?.filter((value) => value)
            .map((src, index) => {
              const isImage = isImageDocument(src);
              const imageViewerIndex = getImageViewerIndex(src);

              return (
                <ListItem
                  key={`${src}-${index}`}
                  component={m.div}
                  onClick={() => {
                    if (isImage && imageViewerIndex >= 0) {
                      openImageViewer(imageViewerIndex);
                    }
                  }}
                  sx={{
                    p: 0,
                    width: 80,
                    height: 80,
                    flex: '0 0 80px',
                    borderRadius: 1.25,
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: isImage ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: (theme) => `solid 1px ${theme.palette.divider}`,
                    bgcolor: 'background.neutral',
                  }}
                >
                  {isImage ? (
                    <Box component='img' alt='preview' src={src} sx={{ width: 1, height: 1, objectFit: 'contain', display: 'block' }} />
                  ) : (
                    <Box sx={{ width: 1, height: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1, textAlign: 'center' }}>
                      <Iconify icon='eva:file-text-outline' sx={{ width: 28, height: 28, color: 'error.main', mb: 0.5 }} />
                      <Button size='small' component='a' href={src} target='_blank' rel='noreferrer' onClick={(event) => event.stopPropagation()} sx={{ minWidth: 0, p: 0, fontSize: 10, lineHeight: 1.2 }}>
                        Open PDF
                      </Button>
                    </Box>
                  )}
                </ListItem>
              );
            })}
        </AnimatePresence>
      </List>
    </Card>
  );
};

export default CashFlowImageUploader;

const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const GENERIC_DOCUMENT_MIME_TYPES = ['', 'application/octet-stream'];

export const isAllowedDocumentFile = (file, allowPdf = false) => {
  const mimeType = String(file?.type || '').toLowerCase();
  const extension = String(file?.name || '').split('.').pop()?.toLowerCase();

  if (ALLOWED_IMAGE_MIME_TYPES.includes(mimeType) || (allowPdf && mimeType === 'application/pdf')) {
    return true;
  }

  if (!GENERIC_DOCUMENT_MIME_TYPES.includes(mimeType)) {
    return false;
  }

  return ALLOWED_IMAGE_EXTENSIONS.includes(extension) || (allowPdf && extension === 'pdf');
};

export const isPdfFile = (file) => file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');

export const isImageDocument = (url = '') => /\.(jpe?g|png)(?:[?#].*)?$/i.test(String(url));
